import { PaymentOptions, SnackBarVariants } from '@app/app.enum';
import * as maerskLogo from '@app/assets/images/maersk-logo-rgb.svg';
import withSnackBar from '@app/components/core/message.component';
import * as models from '@app/models/index';
import Injector from '@app/services';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { Card, CardContent, Grid, Step, StepButton, Stepper, Typography } from '@material-ui/core';
import React, { Component } from 'react';
import CredentialsInfoStepperComponent from './credentials-info-stepper.component';
import PaymentMethodStepperComponent from './payment-method-stepper.component';
import QuickSignUpStepperComponent from './quick-signup-stepper.component';

const intlService: IntlService = Injector.get(IntlService);
const sharedService: SharedService = Injector.get(SharedService);

const Steps: models.IQuickSignUpStepper[] = [
	{
		label: intlService.GetFormattedMessage('app.quick-sign-up.sign-up-label'),
	},
	{
		label: intlService.GetFormattedMessage('app.quick-sign-up.payment-info-label'),
	},
	{
		label: intlService.GetFormattedMessage('app.quick-sign-up.confirmation-label'),
	},
	{
		label: intlService.GetFormattedMessage('app.quick-sign-up.success-label'),
	},
];

const StepsHeader = [
	intlService.GetFormattedMessage('app.quick-sign-up.sign-up-label'),
	intlService.GetFormattedMessage('payment.method.stepper.heading'),
	intlService.GetFormattedMessage('credential.info.stepper.heading'),
	intlService.GetFormattedMessage('app.quick-sign-up.success-part1'),
];

class QuickSignUpComponent extends Component<models.ISnackBarFeature, models.IQuickSignUpComponentState> {
	constructor(props: models.ISnackBarFeature) {
		super(props);
		this.state = {
			isStepLinear: true,
			accessToken: '',
			refreshToken: '',
			app: null,
			activeStep: 0,
			disableForm: false,
			signUpFormData: null,
			paymentDetails: null,
			paymentMethod: null,
			isPaymentApiInvoked: false,
		};
	}

	public componentDidUpdate(prevProps: unknown, prevState: models.IQuickSignUpComponentState) {
		if (prevState.activeStep !== this.state.activeStep && this.state.activeStep === Steps.length - 1) {
			this.setState({
				isStepLinear: false,
			});
		}

		if (prevState.isPaymentApiInvoked !== this.state.isPaymentApiInvoked && this.state.isPaymentApiInvoked) {
			this.props.openSnackBar(intlService.GetFormattedMessage('app.snackbar.profile-payment-method-successfully'));
		}
	}

	public render() {
		return (
			<div className='pb-2 mt-10'>
				<div className='text-center pb-2'>
					<img src={maerskLogo} className='genesis-logo-signin-signup' />
				</div>
				<Grid container={true} justifyContent='center' alignItems='center'>
					<Grid item={true} xs={10} sm={8} md={6}>
						<Card>
							<CardContent>
								{this.renderStepper()}
								<div className='text-center'>
									<Typography component='h3' variant='h6' className={this.state.activeStep === 1 ? 'pb-2' : ''}>
										{StepsHeader[this.state.activeStep]}
									</Typography>
								</div>
								{this.getActiveStepContent(this.state.activeStep)}
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</div>
		);
	}

	private getActiveStepContent = (step: number) => {
		switch (step) {
			case 0:
				return (
					<QuickSignUpStepperComponent
						disableForm={this.state.disableForm}
						disabledFormAction={() => this.updateProcessCount()}
						signUpFormData={this.state.signUpFormData}
						onSignUp={this.onSignUp}
					/>
				);
			case 1:
				return (
					<PaymentMethodStepperComponent
						accessToken={this.state.accessToken}
						paymentInfo={{
							method: this.state.paymentMethod,
							details: this.state.paymentDetails,
						}}
						onSuccess={(paymentMethod: PaymentOptions, paymentDetails: models.IPaymentValueSetUp) => {
							this.updateProcessCount();
							this.setState({
								paymentMethod,
								paymentDetails,
								isPaymentApiInvoked: true,
							});
						}}
						onNext={() => {
							this.setState({
								isPaymentApiInvoked: false,
							});
							this.updateProcessCount();
						}}
					/>
				);
			case 2:
				return (
					<CredentialsInfoStepperComponent
						accessToken={this.state.accessToken}
						app={this.state.app}
						updateStepper={() => this.updateProcessCount()}
					/>
				);
			case 3:
				return (
					<div className='text-center'>
						{[
							intlService.GetFormattedMessage('app.quick-sign-up.success-part2'),
							intlService.GetFormattedMessage('app.quick-sign-up.success-part3'),
						].map((x) => (
							<Typography component='h3' variant='body1' className='pt-2'>
								{x}
							</Typography>
						))}
					</div>
				);
			default:
				return null;
		}
	};

	private onSignUp = (user: models.IUserSignInResponse, signUpFormData?: models.ISignUpFormFields, error?: any) => {
		if (error) {
			return this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
		}

		const {
			accessToken,
			refreshToken,
			apps: [app],
		} = user;

		this.updateStepCompletionStatus(this.state.activeStep);
		this.setState((prevState: models.IQuickSignUpComponentState) => ({
			accessToken,
			refreshToken,
			app,
			signUpFormData,
			activeStep: prevState.activeStep + 1,
		}));
	};

	private updateProcessCount = () => {
		this.updateStepCompletionStatus(this.state.activeStep);
		this.setState((prevState: models.IQuickSignUpComponentState) => ({
			activeStep: prevState.activeStep + 1,
		}));
	};

	private renderStepper = () => {
		return (
			<Stepper
				activeStep={this.state.activeStep}
				alternativeLabel={true}
				classes={{ root: 'mb-2 mt-2' }}
				nonLinear={!this.state.isStepLinear}
			>
				{Steps.map((label, index) => (
					<Step key={index}>{this.renderStepperLabel(label, index)}</Step>
				))}
			</Stepper>
		);
	};

	private renderStepperLabel = (step: models.IQuickSignUpStepper, index: number) => {
		const updateState = () => {
			if (this.state.activeStep === index) {
				return;
			}

			if (index === 0) {
				this.setState({
					disableForm: true,
				});
			}

			Steps.forEach((x, i) => {
				if (i >= index) {
					x.stepCompleted = false;
				}

				if (i < index) {
					x.stepCompleted = true;
				}

				if (index === i && i === Steps.length - 1) {
					x.stepCompleted = true;
				}
			});

			this.setState({
				activeStep: index,
				isPaymentApiInvoked: false,
			});
		};
		return (
			<StepButton completed={!!step.stepCompleted} onClick={() => updateState()}>
				{' '}
				{step.label}
			</StepButton>
		);
	};

	private updateStepCompletionStatus = (step: number) => {
		if (step === Steps.length - 2) {
			Steps[Steps.length - 1].stepCompleted = true;
		}
		Steps[step].stepCompleted = true;
	};
}

export default withSnackBar(QuickSignUpComponent);



// WEBPACK FOOTER //
// ./src/components/account/signup/quick/quick-sign-up.component.tsx